import cn from 'classnames';
import { difference, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { approvedStatusOptions } from '../../../constants/approvedStatus';
import { getActiveFilter, getAvailableTableColumns } from '../../../utils/pricings';
import {
  PRICING_FILTER_CHANNELS_KEY,
  PRICING_FILTER_CURRENCIES_KEY,
  PRICING_FILTER_HAS_PRICE_KEY,
  PRICING_FILTER_SEARCH_KEY,
  PRICING_FILTER_STATUSES_KEY,
  setPricingFilter,
  setPricingHiddenColumns,
} from '../../../store/actions/pricing';
import { usePricingAnalytics } from './usePricingAnalytics';
import { CheckboxFilter } from '../CheckboxFilter';
import { ColumnsOutlineIcon } from '../../icons/ColumnsOutlineIcon';
import { ExportPricingOptionsModal } from '../../modals/ExportPricingOptionsModal';
import { FileDownloadIcon } from '../../icons/FileDownloadIcon';
import { FileUploadIcon } from '../../icons/FileUploadIcon';
import { MultipleFilter } from '../MultipleFilter';
import { PopupToggler } from '../../blocks/PopupToggler';
import { PricingFiltersSummary } from './PricingFiltersSummary';
import { TextFilter } from '../TextFilter';
import { UploadPricingOptionsModal } from '../../modals/UploadPricingOptionsModal';

import styles from './PricingFilters.module.scss';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { tempHideHasPricePricingFilterSupplierIds } from '../../../constants/featureFlag';
import { supplierRetailerUtils } from '../../../utils/supplierRetailer';

const DISABLED_INFO = 'You can only select a single filter';

const getHideHasPriceSupplierIds = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return [];
  }
};

export function PricingFilters(props) {
  const { currencies, supplierChannels, supplierRetailers = [], onExport, onUpload } = props;
  const { reportToAnalytics } = usePricingAnalytics();
  const supplierId = useSelector((state) => state.role.supplierId);
  const hideHasPriceSupplierIdsValue = useFeatureFlag(tempHideHasPricePricingFilterSupplierIds) || [];
  const hideHasPriceSupplierIds = getHideHasPriceSupplierIds(hideHasPriceSupplierIdsValue);

  const onClickUpload = (file, retailerId) => {
    dispatch(onUpload(file, retailerId));
    reportToAnalytics('upload-prices', { fileName: file.name });
  };

  const dispatch = useDispatch();
  const {
    data,
    filters,
    table: { hiddenColumns },
  } = useSelector((state) => state.pricings);

  const activeFilter = useMemo(
    () =>
      getActiveFilter({
        filters: filters,
        availableOptions: {
          currencies,
          channels: supplierChannels,
          statuses: approvedStatusOptions,
        },
      }),
    [filters, currencies, supplierChannels],
  );

  const [popupsByFilterKey, setPopupsByFilterKey] = useState({
    [PRICING_FILTER_CURRENCIES_KEY]: false,
    [PRICING_FILTER_STATUSES_KEY]: false,
    [PRICING_FILTER_CHANNELS_KEY]: false,
  });

  useEffect(
    function setFilters() {
      const data = {};
      if (currencies?.length > 0 && difference(currencies, filters.currencies).length > 0) {
        data[PRICING_FILTER_CURRENCIES_KEY] = currencies;
      }

      if (supplierChannels?.length > 0) {
        data[PRICING_FILTER_CHANNELS_KEY] = supplierChannels.map((channel) => channel.id);
      }

      if (!isEmpty(data)) {
        dispatch(setPricingFilter(data));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currencies, supplierChannels],
  );

  const columns = useMemo(() => getAvailableTableColumns(hiddenColumns), [hiddenColumns]);
  const hasData = data.length > 0;
  const isChannelsFilterAvailable = supplierChannels?.length > 0;

  const updateFilter = (name, value) => {
    if (filters[name] !== value) {
      dispatch(setPricingFilter({ [name]: value }));
    }
  };

  const setHiddenColumns = (selectedColumns) => {
    const hiddenColumns = columns
      .filter((column) => !selectedColumns.includes(column.value))
      .map((column) => column.value);
    dispatch(setPricingHiddenColumns(hiddenColumns));
  };

  const visibleColumns = useMemo(
    () => columns.filter((column) => !hiddenColumns.includes(column.value)).map((column) => column.value),
    [hiddenColumns, columns],
  );

  const currenciesOptions = useMemo(() => {
    return (currencies || []).map((currency) => ({
      value: currency,
      label: currency,
    }));
  }, [currencies]);

  const channelsOptions = useMemo(() => {
    return (supplierChannels || []).map((supplierChannel) => {
      const retailerDisplayName = supplierRetailerUtils.getRetailerDisplayNameFromRetailerId({
        supplierRetailers,
        retailerId: supplierChannel.id,
      });
      return {
        value: supplierChannel.id,
        label: retailerDisplayName || supplierChannel.name,
      };
    });
  }, [supplierChannels, supplierRetailers]);

  const isDisabled = (filterName) => activeFilter !== null && activeFilter !== filterName;

  return (
    <div className={styles.pricingFiltersWrapper}>
      <div className={styles.pricingFilters}>
        <TextFilter
          key={`search_${filters.search}`}
          name="Search"
          placeholder="Search by title, model, SKU, UPC or EAN"
          value={filters.search || ''}
          onChange={(value) => {
            updateFilter(PRICING_FILTER_SEARCH_KEY, value);
            if (value) {
              reportToAnalytics('search');
            }
          }}
          disabled={isDisabled(PRICING_FILTER_SEARCH_KEY)}
          info={isDisabled(PRICING_FILTER_SEARCH_KEY) ? DISABLED_INFO : undefined}
        />
        <div className={styles.filterItem}>
          <PopupToggler
            alignment="right"
            onTriggerClick={(isOpened) =>
              setPopupsByFilterKey((prev) => ({ ...prev, [PRICING_FILTER_STATUSES_KEY]: isOpened }))
            }
            trigger={
              <span
                className={cn(
                  styles.filterTrigger,
                  popupsByFilterKey[PRICING_FILTER_STATUSES_KEY] ? styles.expanded : '',
                )}
              >
                Status
              </span>
            }
            disabled={isDisabled(PRICING_FILTER_STATUSES_KEY)}
            info={isDisabled(PRICING_FILTER_STATUSES_KEY) ? DISABLED_INFO : undefined}
          >
            {(closePopup) => (
              <MultipleFilter
                key={`trigger_${filters.statuses?.join('_')}`}
                selected={filters.statuses}
                options={approvedStatusOptions}
                onReset={closePopup}
                onChange={(value) => {
                  updateFilter(PRICING_FILTER_STATUSES_KEY, value);
                  closePopup();
                  reportToAnalytics('status');
                }}
                withButtons
                withSelectAll
                allowEmpty={false}
                className={styles.multipleFilterWrapper}
              />
            )}
          </PopupToggler>
        </div>

        <div className={styles.filterItem}>
          <PopupToggler
            alignment="right"
            onTriggerClick={(isOpened) =>
              setPopupsByFilterKey((prev) => ({ ...prev, [PRICING_FILTER_CURRENCIES_KEY]: isOpened }))
            }
            trigger={
              <span
                className={cn(
                  styles.filterTrigger,
                  popupsByFilterKey[PRICING_FILTER_CURRENCIES_KEY] ? styles.expanded : '',
                )}
              >
                Currency
              </span>
            }
            disabled={isDisabled(PRICING_FILTER_CURRENCIES_KEY) || currenciesOptions.length === 0}
            info={isDisabled(PRICING_FILTER_CURRENCIES_KEY) ? DISABLED_INFO : undefined}
          >
            {(closePopup) => (
              <MultipleFilter
                key={`trigger_${filters.currencies?.join('_')}`}
                selected={filters.currencies}
                options={currenciesOptions}
                onReset={() => closePopup()}
                onChange={(value) => {
                  updateFilter(PRICING_FILTER_CURRENCIES_KEY, value);
                  closePopup();
                  reportToAnalytics('currencies');
                }}
                withButtons
                withSelectAll
                allowEmpty={false}
                className={styles.multipleFilterWrapper}
              />
            )}
          </PopupToggler>
        </div>

        {isChannelsFilterAvailable && (
          <div className={styles.filterItem}>
            <PopupToggler
              alignment="right"
              onTriggerClick={(isOpened) =>
                setPopupsByFilterKey((prev) => ({ ...prev, [PRICING_FILTER_CHANNELS_KEY]: isOpened }))
              }
              trigger={
                <span
                  className={cn(
                    styles.filterTrigger,
                    popupsByFilterKey[PRICING_FILTER_CHANNELS_KEY] ? styles.expanded : '',
                  )}
                >
                  Channels
                </span>
              }
              disabled={isDisabled(PRICING_FILTER_CHANNELS_KEY)}
              info={isDisabled(PRICING_FILTER_CHANNELS_KEY) ? DISABLED_INFO : undefined}
            >
              {(closePopup) => (
                <MultipleFilter
                  key={`trigger_${filters.channels?.join('_')}`}
                  selected={filters.channels}
                  options={channelsOptions}
                  onReset={() => closePopup()}
                  onChange={(value) => {
                    updateFilter(PRICING_FILTER_CHANNELS_KEY, value);
                    closePopup();
                    reportToAnalytics('channels');
                  }}
                  withButtons
                  withSelectAll
                  allowEmpty={false}
                  className={styles.multipleFilterWrapper}
                  minWidth={250}
                />
              )}
            </PopupToggler>
          </div>
        )}

        {hideHasPriceSupplierIds.includes(supplierId) === false && (
          <div className={styles.filterItem}>
            <CheckboxFilter
              name="Has Price?"
              checked={filters.hasPrice}
              onChange={(checked) => {
                updateFilter(PRICING_FILTER_HAS_PRICE_KEY, checked);
                reportToAnalytics('has-price');
              }}
              disabled={isDisabled(PRICING_FILTER_HAS_PRICE_KEY)}
              info={isDisabled(PRICING_FILTER_HAS_PRICE_KEY) ? 'You can only select a single filter' : undefined}
            />
          </div>
        )}

        <div className={styles.selectColumnsWrapper}>
          <PopupToggler icon={<FileUploadIcon />} wide hideCaret>
            {(closePopup) => <UploadPricingOptionsModal onUpload={onClickUpload} onClose={closePopup} />}
          </PopupToggler>
          <PopupToggler icon={<FileDownloadIcon />} wide hideCaret>
            {(closePopup) => <ExportPricingOptionsModal onExport={onExport} onClose={closePopup} />}
          </PopupToggler>
          {hasData && (
            <PopupToggler icon={<ColumnsOutlineIcon />} hideCaret>
              <MultipleFilter
                key={columns}
                selected={visibleColumns}
                options={columns}
                onChange={setHiddenColumns}
                className={styles.multipleFilterWrapper}
                name="Select Columns to present"
                minWidth={210}
              />
            </PopupToggler>
          )}
        </div>
      </div>

      <div className={styles.filtersSummaryWrapper}>
        <PricingFiltersSummary
          currencies={currencies}
          channels={supplierChannels}
          isChannelsFilterAvailable={isChannelsFilterAvailable}
        />
      </div>
    </div>
  );
}

PricingFilters.propTypes = {
  currencies: PropTypes.arrayOf(PropTypes.string),
  supplierChannels: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
  onExport: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
};
